import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ShieldCheck, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../config/api';
import toast from 'react-hot-toast';

const ForgotPassword = () => {
  const [form, setForm] = useState({
    email: '',
    otp: '',
    password: '',
    confirmPassword: '',
  });
  const [step, setStep] = useState('request');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!cooldown) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = () => {
    if (!form.email.trim()) {
      toast.error('Please enter your email address.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Please enter a valid email address.');
      return false;
    }
    return true;
  };

  const validatePasswords = () => {
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match.');
      return false;
    }
    return true;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;

    try {
      setLoading(true);
      await api.post('/otp/send-reset-otp', { email: form.email.trim() });
      toast.success('OTP sent! Please check your email.');
      setStep('verify');
      setCooldown(60);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send OTP';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    if (!form.otp.trim()) {
      toast.error('Please enter the OTP sent to your email.');
      return;
    }
    if (!validatePasswords()) return;

    try {
      setLoading(true);
      await api.post('/otp/verify-reset-otp', {
        email: form.email.trim(),
        otp: form.otp.trim(),
        newPassword: form.password,
      });
      toast.success('Password reset successful! Please sign in.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reset password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown) return;
    if (!validateEmail()) return;
    try {
      await api.post('/otp/send-reset-otp', { email: form.email.trim() });
      toast.success('OTP resent! Please check your email.');
      setCooldown(60);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to resend OTP';
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex flex-col">
      <Header />
      <div className="container mx-auto px-4 py-16 flex-1">
        <div className="max-w-lg mx-auto">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Sign In
          </Link>

          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className="text-4xl font-black tracking-tight leading-none select-none">
                <span className="text-[#ff405c]">my</span>
                <span className="text-[#5c3fd8]">STORY</span>
                <span className="text-[#ff405c]">me</span>
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {step === 'request' ? 'Forgot Password?' : 'Verify & Reset'}
            </h1>
            <p className="text-gray-600">
              {step === 'request'
                ? 'Enter your registered email address and we will send you a verification code.'
                : 'Enter the OTP we sent to your email and set a new password.'}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {step === 'request' ? (
              <form onSubmit={handleSendOtp} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none border-gray-200 focus:border-purple-500"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyAndReset} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        disabled
                        className="w-full pl-10 pr-4 py-3 border-2 rounded-lg bg-gray-50 border-gray-200 text-gray-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Code
                    </label>
                    <div className="relative">
                      <ShieldCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        name="otp"
                        value={form.otp}
                        onChange={handleChange}
                        maxLength={6}
                        className="w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none border-gray-200 focus:border-purple-500 tracking-widest uppercase"
                        placeholder="123456"
                        required
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={!!cooldown}
                      className="mt-2 text-sm font-semibold text-purple-600 hover:text-purple-700 disabled:text-gray-400"
                    >
                      {cooldown ? `Resend code in ${cooldown}s` : 'Resend code'}
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type={showPasswords.password ? 'text' : 'password'}
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        className="w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:outline-none border-gray-200 focus:border-purple-500"
                        placeholder="********"
                        required
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPasswords((prev) => ({
                            ...prev,
                            password: !prev.password,
                          }))
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={showPasswords.password ? 'Hide password' : 'Show password'}
                      >
                        {showPasswords.password ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type={showPasswords.confirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        className="w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:outline-none border-gray-200 focus:border-purple-500"
                        placeholder="********"
                        required
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPasswords((prev) => ({
                            ...prev,
                            confirmPassword: !prev.confirmPassword,
                          }))
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={showPasswords.confirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPasswords.confirmPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Resetting password...' : 'Reset Password'}
                </button>
              </form>
            )}
          </div>

          <div className="mt-6 text-center text-sm text-gray-600">
            Remembered your password?{' '}
            <Link to="/login" className="text-purple-600 font-semibold hover:text-purple-700">
              Go back to sign in
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForgotPassword;

